import React, { useEffect } from "react";
import { Redirect, Route, RouteComponentProps } from "react-router-dom";
import AuthContainer from "../../../../shared/state/containers/AuthContainer";
import { Subscribe } from "unstated";
import { css } from '@emotion/css';
import OrganizationContainer from "../../../../shared/state/containers/OrganizationContainer";
import Splash from "../../../../shared/scenes/splash";
import ApplicationContainer from "../../../../shared/state/containers/ApplicationContainer";
import OrganizationMemberInvite from "../../../../shared/scenes/organizationMemberInvite";
import UserContainer from "../../../../shared/state/containers/UserContainer";
import FeatureContainer from "../../../../shared/state/containers/FeatureContainer";
import BasicOrganizationDashboard from "../basicOrganizationDashboard";
import Logout from "../../../../shared/scenes/logout";
import ApplicationManager from "./ApplicationManager";
import Loader from "../../../../shared/components/general/Loader";
import ApplicationAnalyticsFacetContainer from "../../../../shared/state/containers/ApplicationAnalyticsFacetContainer";
import NotificationsContainer from "../../../../shared/hooks/NotificationsContainer";
import BasicReviewDashboard from "../reviewDashboard";
import CrossOrgSessionReview from "../../../../shared/scenes/crossOrgSessionReview";
import { isVoicifyUser } from "../../../../shared/utils/userAccess";

const Dashboard: React.FC<RouteComponentProps> = (props) => {
    return <Subscribe to={[AuthContainer,
        UserContainer,
        OrganizationContainer,
        ApplicationContainer,
        FeatureContainer,
        ApplicationAnalyticsFacetContainer
    ]}>
        {(auth: AuthContainer,
            userContainer: UserContainer,
            orgs: OrganizationContainer,
            appContainer: ApplicationContainer,
            featureContainer: FeatureContainer,
        ) => {
            if ((props.location.pathname?.toLowerCase().includes('/review/') && !props.location.pathname.includes('/v/')) ||
                props.location.pathname?.toLowerCase().includes('/fullReview/') && !props.location.pathname.includes('/v/')) {
                return <InnerDashboard
                    authContainer={auth}
                    userContainer={userContainer}
                />
            }
            // if we are logged in, go to root
            if (auth.state.isAuthenticated !== true || auth.state.expirationDate === undefined) {
                const fullPath = window.location.pathname + window.location.search;
                if (fullPath.length > 1 && !fullPath.startsWith('/login') && fullPath !== '/v' && fullPath !== "/v/logout") {
                    sessionStorage.setItem('VOICIFY_POST_LOGIN_REDIRECT', fullPath);
                }
                return <Redirect to="/login" />
            }
            // if we have no current org, go to orgs page
            if (orgs.state.currentOrganization === null || !orgs.state.organizations?.length || auth.state.expirationDate < new Date()) {
                return <Splash
                    authContainer={auth}
                    orgContainer={orgs}
                    appContainer={appContainer}
                    featureContainer={featureContainer} />
            }
            if (props.location.pathname.toLocaleLowerCase().includes('/crossorgsessionreview')) {
                return <CrossOrgSessionReview
                    userContainer={userContainer}
                    organizationContainer={orgs}
                    appContainer={appContainer}
                />;
            }
            if (props.location.pathname.toLocaleLowerCase().includes('/organizationreview')) {
                return <CrossOrgSessionReview
                    userContainer={userContainer}
                    organizationContainer={orgs}
                    appContainer={appContainer}
                    currentOrgMode
                />;
            }
            return <InnerDashboard
                authContainer={auth}
                userContainer={userContainer}
            />
        }
        }
    </Subscribe>

};

interface InnerDashboardProps {
    userContainer: UserContainer
    authContainer: AuthContainer
}

const InnerDashboard: React.FC<InnerDashboardProps> = ({ userContainer, authContainer }) => {

    const notificationsHook = NotificationsContainer.useContainer();
    const shouldShowNotifications = isVoicifyUser(userContainer.state.currentUser);
    useEffect(() => {
        if (authContainer.state.isAuthenticated && !authContainer.state.accessToken && !authContainer.state.apiKeyAuth) {
            authContainer.refreshToken();
        }
        if (shouldShowNotifications) {
            notificationsHook.getNotifications();
        }

        const targetDirectApplication = sessionStorage.getItem('invite_target_application_id')
        if (targetDirectApplication) {
            window.location.href = `/v/apps/${targetDirectApplication}`
            sessionStorage.removeItem('invite_target_application_id')
        }
    }, []);

    if (authContainer.state.accessToken == null && !window.location.pathname?.includes('/review/') && !window.location.pathname?.includes('/fullReview/')) {
        return <Loader />
    };

    return (
        <div className={wrapperStyle}>
            <div className={pageStyle}>
                <div className={containerStyle}>
                    <Route path="/v" exact component={BasicOrganizationDashboard} />
                    <Route path="/" exact component={BasicOrganizationDashboard} />
                    <Route path="/v/members" component={BasicOrganizationDashboard} />
                    <Route path="/v/members/invite" exact component={OrganizationMemberInvite} />
                    <Route path="/v/orgSettings" component={BasicOrganizationDashboard} />
                    <Route path="/v/orgSettings/applications" exact component={BasicOrganizationDashboard} />
                    <Route path="/v/apps/:applicationId" component={ApplicationManager} />
                    <Route path="/review/:applicationId" exact component={BasicReviewDashboard} />
                    <Route path="/fullReview/:applicationId" exact component={BasicReviewDashboard} />
                </div>
            </div>
            <Route path="/v/logout" exact component={Logout} />
        </div>
    );
};

const containerStyle = css`
    display:flex;
    flex-direction:row;
    max-height: 100%;
    height: 100%;
`;

const pageStyle = css`
    position:fixed;
    width: 100%;
    height: 100%;
`;

const wrapperStyle = css`
    height: 100%;
`;

export default Dashboard;
